import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import type { Language, SupportedMarketKey } from '@nxt-insurance/constants'

import { ControlledDateTimePicker } from '@flows-ui/helperComponents/ControlledDateTimePicker'
import { useFlowsServicesResponses } from '@flows-ui/services/FlowServicesResponsesContext'
import { processStepContentElementText } from '@flows-ui/utils/processStepContentElementText'
import type { AnyCallbackConfig } from '@flows/core/types/dispatchedHookTypes'
import type { Date as DateProps } from '@flows/core/types/stepContentTypes'
import type { AnySimpleOnSubmitHook, FlowValues } from '@flows/core/types/stepTypes'

interface Props<
  TCallbackConfig extends AnyCallbackConfig,
  TSimpleOnSubmitHook extends AnySimpleOnSubmitHook,
> extends DateProps<TCallbackConfig, TSimpleOnSubmitHook> {
  flowValues: FlowValues
  locale: `${Language}-${SupportedMarketKey}`
}

export const Date = <TProps extends Props<any, any>>(props: TProps) => {
  const { t } = useTranslation()

  const placeholders = {
    day: t('generic.inputs.day'),
    month: t('generic.inputs.month'),
    year: t('generic.inputs.year'),
  }

  const { control } = useFormContext()
  const servicesResponses = useFlowsServicesResponses()
  const textProcessor = processStepContentElementText(t, {
    flowValues: props.flowValues,
    servicesResponses,
  })

  return (
    <ControlledDateTimePicker
      placeholders={placeholders}
      locale={props.locale}
      label={props.label ? textProcessor(props.label) : undefined}
      disabled={props.disabled}
      name={props.name}
      control={control}
      hideDay={props.hideDay}
      hideMonth={props.hideMonth}
      hideYear={props.hideYear}
      autoFocus={props.autoFocus}
      styleType={props.styleType}
      value={props.value as string}
      onValueChange={props.onValueChange}
    />
  )
}
